import React, { useEffect, useState } from "react";
import { IoIosArrowRoundBack } from "react-icons/io";
import Modal from "../../components/modal/Modal";
import AddAgentForm from "../../components/directory/addAgentForm";
import CompanyDirectoryEditView from "../../components/companies/CompanyDirectoryEditView";
import useConfirmDialog from "../../components/ConfirmDialog/useConfirmDialog";
import TableToolbar from "../../components/TableToolbar/TableToolbar";
import { compareText, matchesSearch } from "../../utils/tableSort";
import Breadcrumb from "../../components/Breadcrumb";
import { GET_AGENTS } from "../../graphql/directoryQueries";
import {
  DELETE_CONTACTS,
  GET_CONTACTS_BY_SUBCOMPANY,
} from "../../graphql/contactQuries";
import { useMutation, useQuery } from "@apollo/client";
import { BsFiletypeCsv } from "react-icons/bs";
import { useToast } from "../../components/Toast/toastHook";
import { Button, Table } from "antd";
import {
  PlusOutlined as PlusIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
} from "@ant-design/icons";
import { TABLE_SCROLL } from "../../constants/table";

// Contacts arrive with the sub-company, so search and sort run in the browser.
const CONTACT_SEARCH_FIELDS = ["name", "position", "email", "phone", "address"];

const CompanyView = ({
  companyId,
  handleBackClick,
  companyName,
  details,
  directoryName,
  subCompanyId,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [formType, setFormType] = useState("add");
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [agentId, setAgentId] = useState(null);

  const [temp, setTemp] = useState("");

  const [getDetails, setGetDetails] = useState([]);
  const [contactSearch, setContactSearch] = useState("");

  const toast = useToast(4000);
  const { confirm, dialog } = useConfirmDialog();
  const [deleteContact] = useMutation(DELETE_CONTACTS);

  const { data: getContactsBySubCompany, refetch: refetchAgentData } = useQuery(
    GET_CONTACTS_BY_SUBCOMPANY,
    {
      variables: { subCompanyId },
      fetchPolicy: "no-cache",
      onCompleted(result) {
        // console.log(result);
        // console.log("result");
        // setGetDetails(result?.getDirectoryList?.[0]?.details);
      },
    }
  );

  useEffect(() => {
    if (getContactsBySubCompany?.getContactsBySubCompany) {
      setTemp(getContactsBySubCompany?.getContactsBySubCompany?.name);
      setGetDetails(getContactsBySubCompany?.getContactsBySubCompany?.contacts);
    }
  }, [getContactsBySubCompany]);

  const handleEditModalRender = (agentToEdit) => {
    setFormType("edit");
    setSelectedAgent(agentToEdit);
    setAgentId(agentToEdit._id);
    setShowEditModal(true);
  };

  const handleDelete = (contact) =>
    confirm({
      title: `Delete ${contact.name}?`,
      description:
        "This contact will be permanently deleted. This action cannot be undone.",
      onConfirm: async () => {
        try {
          const { data } = await deleteContact({
            variables: { contactId: contact._id },
          });
          if (data) {
            toast("success", "Contact deleted successfully");
          } else {
            toast("error", "Contact not deleted or does not exist");
          }
        } catch (error) {
          toast("error", "Failed to delete contact");
        }
        refetchAgentData({ companyId });
      },
    });

  const handleClose = () => {
    setShowModal(false);
    refetchAgentData({ companyId });
  };

  const COLUMN_MAPPINGS = {
    name: "Contact Name",
    position: "Title",
    address: "Address",
    email: "Email",
    phone: "Primary Contact Number",
    url: "Website",
  };

  const columns = [
    {
      title: "Contact Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => compareText(a.name, b.name),
      render: (text) => text || "No Name",
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      sorter: (a, b) => compareText(a.address, b.address),
      render: (text) => text || "No Address",
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      key: "phone",
      sorter: (a, b) => compareText(a.phone, b.phone),
      render: (text) => text || "No Phone Number",
    },
    {
      title: "Website",
      dataIndex: "url",
      key: "url",
      sorter: (a, b) => compareText(a.url, b.url),
      render: (text) => text || "No Website",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex items-center space-x-2">
          <Button
            className="border-primary text-primary"
            icon={<EditIcon />}
            onClick={() => handleEditModalRender(record)}
          />
          <Button
            danger
            icon={<DeleteIcon />}
            onClick={() => handleDelete(record)}
          />
        </div>
      ),
    },
  ];

  const handleExportAllColumns = () => {
    exportContacts(Object.keys(COLUMN_MAPPINGS));
  };

  const exportContacts = (checkedColumns) => {
    if (getDetails.length === 0) {
      toast("error", "No contacts exists.");
      return;
    }

    try {
      const contactsCSVData = [];
      getDetails.forEach((details) => {
        const columnsData = {};
        for (const column of checkedColumns) {
          switch (column) {
            case "name":
              columnsData[COLUMN_MAPPINGS[column]] = details[column];
              break;
            case "position":
              columnsData[COLUMN_MAPPINGS[column]] = details[column];
              break;
            case "address":
              columnsData[COLUMN_MAPPINGS[column]] = details[column];
              break;
            case "email":
              columnsData[COLUMN_MAPPINGS[column]] = details[column];
              break;
            case "phone":
              columnsData[COLUMN_MAPPINGS[column]] = details[column];
              break;
            case "url":
              columnsData[COLUMN_MAPPINGS[column]] = details[column];
              break;
            default:
              break;
          }
        }
        contactsCSVData.push(columnsData);
      });

      const csvData = contactsCSVData.map((user) =>
        Object.values(user)
          .map((value) => JSON.stringify(value))
          .join(",")
      );

      const csvHeaders = Object.keys(contactsCSVData[0]);
      csvData.unshift(csvHeaders.join(","));

      const csvContent = csvData.join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "contacts_data.csv";

      a.click();

      window.URL.revokeObjectURL(url);

      toast("success", "Contacts data exported successfully.");
    } catch (error) {
      console.log("error in exportContacts", error.message);
      toast("error", "There was an error in exporting contacts.");
    }
  };

  const visibleContacts = (getDetails ?? []).filter((contact) =>
    matchesSearch(contact, CONTACT_SEARCH_FIELDS, contactSearch)
  );

  return (
    <>
      <div
        onClick={handleBackClick}
        className="w-full cursor-pointer flex items-center flex-row gap-4"
      >
        <IoIosArrowRoundBack color="#212636" size={30} />
        <p className="font-normal text-[20px] text-[#212636]">Back</p>
      </div>
      <div className="w-full pt-10 flex flex-col ">
        <Breadcrumb
          pageName="Service -"
          span={companyName + " -"}
          company={temp}
        />
        <div className="mb-4 flex justify-between items-center flex-wrap gap-6">
          <div className="flex justify-start items-center flex-wrap gap-6">
            <div className="font-medium text-black">Export Contacts:</div>
            <div>
              <BsFiletypeCsv
                size={40}
                className="hover:cursor-pointer hover:text-blue"
                onClick={handleExportAllColumns}
              />
            </div>
          </div>
        </div>

        <TableToolbar
          title="Contacts"
          count={visibleContacts.length}
          searchValue={contactSearch}
          onSearch={setContactSearch}
          action={
            <button
              onClick={() => {
                setFormType("add");
                setShowModal(true);
              }}
              className="bg-[#B4C540] h-[36px] px-5 text-center rounded-xl text-[#FFFFFF]"
            >
              + Add Contact
            </button>
          }
        />
        <Table
          scroll={TABLE_SCROLL}
          bordered
          rowKey={(record) => record._id}
          columns={columns}
          dataSource={visibleContacts}
          pagination={{
            position: ["bottomCenter"],
          }}
        />
        <Modal
          title={"Add Contacts Information"}
          showModal={showModal}
          setShowModal={(value) => {
            setShowModal(value);
          }}
        >
          <AddAgentForm
            companyId={companyId}
            handleClose={handleClose}
            onDirectoryListAdded={() => {}}
            formType={formType}
          />
        </Modal>
        <Modal
          title={"Edit Contacts Information"}
          showModal={showEditModal}
          setShowModal={(value) => {
            setShowEditModal(value);
          }}
        >
          <CompanyDirectoryEditView
            companyId={companyId}
            agentId={agentId}
            initialValues={selectedAgent}
            refetchData={() => {}}
            handleClose={() => {
              refetchAgentData({ companyId });
              setShowEditModal(false);
            }}
          />
        </Modal>

        {dialog}
      </div>
    </>
  );
};
export default CompanyView;
